import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Rating,
  Alert,
  LinearProgress,
  Container,
  Pagination
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Business,
  Phone,
  Email,
  LocationOn,
  Receipt,
} from '@mui/icons-material';
import { suppliersAPI, purchaseAPI } from '../../services/API';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const SupplierDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Pagination state for purchase orders
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchSupplierDetails();
  }, [id]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchPurchaseOrders();
    }
  }, [tabValue, pagination.page]);

  const fetchSupplierDetails = async () => {
    try {
      const [supplierResponse, performanceResponse] = await Promise.all([
        suppliersAPI.getById(id),
        suppliersAPI.getPerformance(id),
      ]);

      setSupplier(supplierResponse.data.data.supplier);
      setPerformance(performanceResponse.data.data.performance);
    } catch (error) {
      console.error('Error fetching supplier details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await purchaseAPI.getAll({
        supplierId: id,
        page: pagination.page,
        limit: pagination.limit,
      });
      const { purchaseOrders: ordersData, pagination: paginationData } = response.data.data;
      
      setPurchaseOrders(ordersData);
      setPagination(prev => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const getInitials = (name) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };

  const getPaymentTermsColor = (terms) => {
    const colors = {
      advance: 'success',
      '7_days': 'info',
      '15_days': 'primary',
      '30_days': 'warning',
      '45_days': 'warning',
      '60_days': 'error',
    };
    return colors[terms] || 'default';
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!supplier) {
    return (
      <Alert severity="error">
        Supplier not found
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/pharmacy/suppliers')}
            variant="outlined"
          >
            Back to Suppliers
          </Button>
          <Typography variant="h3" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Supplier Details
          </Typography>
          <Button
            startIcon={<Edit />}
            variant="contained"
            onClick={() => navigate(`/hospital/pharmacy/suppliers/${id}/edit`)}
          >
            Edit Supplier
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Supplier Information */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {getInitials(supplier.name)}
                </Avatar>

                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {supplier.name}
                </Typography>

                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {supplier.contactPerson}
                </Typography>

                <Rating value={supplier.rating} readOnly sx={{ mb: 2 }} />

                <Chip
                  label={supplier.isActive ? 'Active' : 'Inactive'}
                  color={supplier.isActive ? 'success' : 'default'}
                  sx={{ mb: 2 }}
                />

                {/* Contact Information */}
                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="h6">
                      {supplier.phone}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="h6">
                      {supplier.email}
                    </Typography>
                  </Box>
                  {supplier.alternatePhone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="h6">
                        Alt: {supplier.alternatePhone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Business Information */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Business Information
                </Typography>

                <Box sx={{ space: 2 }}>
                  {supplier.drugLicenseNumber && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" color="text.secondary">
                        Drug License
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {supplier.drugLicenseNumber}
                      </Typography>
                    </Box>
                  )}
                  {supplier.gstNumber && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" color="text.secondary">
                        GST Number
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {supplier.gstNumber}
                      </Typography>
                    </Box>
                  )}
                  {supplier.panNumber && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" color="text.secondary">
                        PAN Number
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {supplier.panNumber}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" color="text.secondary">
                      Payment Terms
                    </Typography>
                    <Chip
                      label={supplier.paymentTerms.replace('_', ' ')}
                      color={getPaymentTermsColor(supplier.paymentTerms)}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      Lead Time
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {supplier.leadTime} days
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Performance & Orders */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Performance" />
                  <Tab label="Purchase Orders" />
                  <Tab label="Address" />
                </Tabs>

                {/* Performance Tab */}
                <TabPanel value={tabValue} index={0}>
                  {performance ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" color="primary.main">
                              {performance.totalOrders}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              Total Orders
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" color="success.main">
                              {performance.completedOrders}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              Completed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" color="warning.main">
                              ₹{(performance.totalSpent / 100000).toFixed(1)}L
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              Total Spent
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" color="info.main">
                              {performance.averageLeadTime}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              Avg. Lead Time (Days)
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Category Spending */}
                      {performance.categorySpending && Object.keys(performance.categorySpending).length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="h5" gutterBottom>
                            Spending by Category
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Category</TableCell>
                                  <TableCell align="right">Amount Spent</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(performance.categorySpending).map(([category, amount]) => (
                                  <TableRow key={category}>
                                    <TableCell>
                                      <Typography variant="h6" fontWeight="medium">
                                        {category}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="h6" color="success.main">
                                        ₹{amount.toLocaleString()}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No performance data available
                    </Typography>
                  )}
                </TabPanel>

                {/* Purchase Orders Tab */}
                <TabPanel value={tabValue} index={1}>
                  {purchaseOrders.length > 0 ? (
                    <>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>PO Number</TableCell>
                              <TableCell>Order Date</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {purchaseOrders.map((order) => (
                              <TableRow key={order._id}>
                                <TableCell>
                                  <Typography variant="h6">
                                    {order.poNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6">
                                    {new Date(order.orderDate).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="h6">
                                    ₹{order.grandTotal}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={order.status.replace('_', ' ')}
                                    size="small"
                                    color={
                                      order.status === 'completed' ? 'success' :
                                        order.status === 'cancelled' ? 'error' : 'primary'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    startIcon={<Receipt />}
                                    onClick={() => navigate(`/hospital/pharmacy/purchases/${order._id}`)}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination for Purchase Orders */}
                      {pagination.pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination
                            count={pagination.pages}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No purchase orders found
                    </Typography>
                  )}
                </TabPanel>

                {/* Address Tab */}
                <TabPanel value={tabValue} index={2}>
                  {supplier.address ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <LocationOn color="primary" />
                          <Box>
                            <Typography variant="h5" fontWeight="medium" gutterBottom>
                              {supplier.address.street}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              {supplier.address.city}, {supplier.address.state}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              {supplier.address.zipCode}, {supplier.address.country}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      No address information available
                    </Typography>
                  )}
                </TabPanel>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};